import { useMemo, useState } from 'react'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import Card from '../../../components/ui/Card'
import SelectField from '../../../components/ui/SelectField'
import TextField from '../../../components/ui/TextField'
import { OnboardingStep } from './steps'

type Release = {
  song_title: string
  release_date: string
  release_status: 'Upcoming' | 'Past' | 'Demo'
  primary_artists: string[]
}

function mapStatus(s: Release['release_status']) {
  if (s === 'Past') return 'released'
  if (s === 'Demo') return 'demo'
  return 'unreleased'
}

export default function Step05AddReleases({
  setStep,
  user,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
}) {
  const [releases, setReleases] = useState<Release[]>([
    { song_title: '', release_date: '', release_status: 'Upcoming', primary_artists: [''] },
  ])

  const canNext = useMemo(() => {
    const first = releases[0]
    return !!(first.song_title.trim() && (first.primary_artists[0] || '').trim())
  }, [releases])

  const addRelease = () =>
    setReleases((prev) => [...prev, { song_title: '', release_date: '', release_status: 'Upcoming', primary_artists: [''] }])

  const removeRelease = (idx: number) =>
    setReleases((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev))

  const saveAndNext = () => {
    const normalized = releases
      .map((r) => ({
        song_title: r.song_title.trim(),
        primary_artist: r.primary_artists.filter((a) => a && a.trim()).join(', '),
        release_date: r.release_date || null,
        status: mapStatus(r.release_status),
      }))
      .filter((r) => r.song_title && r.primary_artist)

    localStorage.setItem('pendingCatalogReleases', JSON.stringify(normalized))
    setStep(OnboardingStep.Avatar)
  }

  return (
    <OnboardingShell
      title="Let’s add your catalog"
      subtitle="Add at least one release. You can always edit later."
      footer="We’ll save these releases after your account is created."
    >
      <div className="space-y-4">
        <div className="text-sm text-gray-500">Artist: {user?.name || user?.username || '—'}</div>

        <div className="max-h-[45vh] overflow-y-auto space-y-4 pr-1">
          {releases.map((r, idx) => (
            <Card key={idx} className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-900">Release {idx + 1}</div>
                {releases.length > 1 ? (
                  <button type="button" className="text-sm text-red-600" onClick={() => removeRelease(idx)}>
                    Remove
                  </button>
                ) : null}
              </div>

              <TextField
                value={r.song_title}
                onChange={(v) => setReleases((prev) => prev.map((x, i) => (i === idx ? { ...x, song_title: v } : x)))}
                placeholder="Song title"
              />

              <div className="grid grid-cols-2 gap-3">
                <TextField
                  value={r.primary_artists[0] || ''}
                  onChange={(v) =>
                    setReleases((prev) => prev.map((x, i) => (i === idx ? { ...x, primary_artists: [v] } : x)))
                  }
                  placeholder="Primary artist"
                />
                <SelectField
                  value={r.release_status}
                  onChange={(v) =>
                    setReleases((prev) => prev.map((x, i) => (i === idx ? { ...x, release_status: v as any } : x)))
                  }
                  options={[
                    { label: 'Upcoming', value: 'Upcoming' },
                    { label: 'Past', value: 'Past' },
                    { label: 'Demo', value: 'Demo' },
                  ]}
                />
              </div>

              <TextField
                type="date"
                value={r.release_date}
                onChange={(v) => setReleases((prev) => prev.map((x, i) => (i === idx ? { ...x, release_date: v } : x)))}
              />
            </Card>
          ))}
        </div>

        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={addRelease} className="text-sm text-gray-500 hover:text-gray-900">
            + Add another
          </button>
          <button type="button" onClick={() => setStep(OnboardingStep.Username)} className="text-sm text-gray-500 hover:text-gray-900">
            Go back
          </button>
        </div>

        <button type="button" disabled={!canNext} onClick={saveAndNext} className="ds-btn ds-btn-primary w-full">
          Next
        </button>
      </div>
    </OnboardingShell>
  )
}

